import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  ListRow,
  getAutoScrollDirection,
  getIntersection,
  getRowAtY,
  getRowsInBand,
  getSelectionRectStyle,
  getVisibleBounds,
  isPointerInsideContainer,
  measureRows,
} from '../utils';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { useNavigate } from 'react-router-dom';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import DataRoomSelectedDocumentsContext from '../../../providers/DataRoomSelectedDocuments/DataRoomSelectedDocuments.context';
import { useTranslation } from 'react-i18next';
import DataRoomDocumentsContext from '../../../providers/DataRoomDocuments/DataRoomDocuments.context';
import { isDataRoomViewer } from 'router/subrouters/DataRoom/utils';
import {
  getDataRoomRoute,
  getHasElementsInArray,
  getIsNodeFile,
  getIsNodeFolder,
  getScrollParent,
  isOpenDrawerOrModal,
} from 'utils';
import { SMOOTH_SCROLL_CONFIGURATION } from 'data/global';

const AUTO_SCROLL_STEP = 30;
const AUTO_SCROLL_EDGE = 40;

type Pointer = Pick<MouseEvent, 'clientX' | 'clientY'>;

export default function useSelectAndDeselect() {
  const [isDragging, setIsDragging] = useState(false);
  const [isSelectioningOnDrag, setIsSelectioningOnDrag] = useState(false);
  const [isRequestPending, setIsRequestPending] = useState(false);

  const timeRef = useRef<NodeJS.Timeout | number>();
  const autoScrollDirection = useRef(0);
  // List-relative, so scrolling mid-drag doesn't move the anchor.
  const selectionStart = useRef<{ x: number; y: number }>(null);
  const selectedIdsKey = useRef('');
  const lastPointer = useRef<Pointer>(null);
  const rows = useRef<ListRow[]>([]);
  const scrollParent = useRef<HTMLElement>(null);
  const docListRef = useRef<HTMLDivElement>(null);
  const draggableOverlayRef = useRef<HTMLDivElement>(null);
  const selectionRectRef = useRef<HTMLDivElement>(null);
  const highlightedRow = useRef<HTMLElement>(null);

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const { currentlyDisplayedNodes } = useContext(DataRoomDocumentsContext);
  const { selected, setSelected } = useContext(
    DataRoomSelectedDocumentsContext,
  );
  const {
    role,
    dataRoom: { _id },
  } = useContext(DataRoomContext);

  const navigate = useNavigate();

  const { t } = useTranslation();

  const onDragMove = useCallback(
    async (nodeToMoveTo: DataRoomNode) => {
      if (getIsNodeFile(nodeToMoveTo)) return;

      const filesIds = selected
        .filter(({ _id }) => _id !== nodeToMoveTo?._id)
        .map(({ _id }) => _id);

      if (!filesIds.length) return;

      try {
        setIsRequestPending(true);

        await dataRoomApi.nodes.moveFilesToFolder(filesIds, nodeToMoveTo._id);

        popSuccess(
          t('successfullyMovedFilesToThisFolder', {
            count: selected.length,
            folderName: nodeToMoveTo.name,
          }),
        );

        navigate(
          `${getDataRoomRoute(_id, 'documents')}/${nodeToMoveTo._id || ''}`,
        );
      } catch (e) {
        popServerError(e);
      } finally {
        setIsRequestPending(false);
      }
    },
    [selected, popSuccess, t, navigate, _id, popServerError],
  );

  // Selects the rows between the drag's start and `y`, both list-relative.
  const selectOnDrag = useCallback(
    (y: number) => {
      const nodes = getRowsInBand(
        rows.current,
        selectionStart.current.y,
        y,
      ).map(({ node }) => node);

      const idsKey = nodes.map(({ _id }) => _id).join();

      // Moving within the same rows mustn't re-render the list.
      if (idsKey === selectedIdsKey.current) return;

      selectedIdsKey.current = idsKey;
      setSelected(nodes);
    },
    [setSelected],
  );

  const drawSelectionRect = useCallback((pointer: Pointer, rect: DOMRect) => {
    const { x, y } = selectionStart.current;

    selectionRectRef.current?.setAttribute(
      'style',
      getSelectionRectStyle(
        { clientX: rect.left + x, clientY: rect.top + y },
        pointer,
        getIntersection(rect, getVisibleBounds(scrollParent.current)),
      ),
    );
  }, []);

  // Only the row highlighted on the previous move can carry the class, so this
  // stays O(1) instead of walking every row on every mousemove.
  const resetContainerStyles = useCallback(() => {
    draggableOverlayRef?.current?.classList?.remove('accepted');
    highlightedRow.current?.classList.remove('accepted-div-for-dropping');
    highlightedRow.current = null;
  }, []);

  const prepareDraggableStyle = useCallback(
    (pointer: Pointer) => {
      const { clientX, clientY } = pointer;

      const rect = docListRef.current?.getBoundingClientRect();

      if (!rect) return;

      const y = clientY - rect.top;

      if (isSelectioningOnDrag) {
        selectOnDrag(y);
        drawSelectionRect(pointer, rect);
      }

      if (isDragging && isPointerInsideContainer(pointer, rect)) {
        const row = getRowAtY(rows.current, y);

        resetContainerStyles();

        if (getIsNodeFolder(row?.node)) {
          draggableOverlayRef?.current?.classList?.add('accepted');
          row.element.classList.add('accepted-div-for-dropping');
          highlightedRow.current = row.element;
        }
      } else {
        resetContainerStyles();
      }

      draggableOverlayRef?.current?.setAttribute(
        'style',
        `top:${clientY}px; left:${clientX + 4}px;`,
      );
    },
    [
      isDragging,
      isSelectioningOnDrag,
      selectOnDrag,
      drawSelectionRect,
      resetContainerStyles,
    ],
  );

  const stopAutoScroll = useCallback(() => {
    clearInterval(timeRef.current as number);
    autoScrollDirection.current = 0;
  }, []);

  // The list grows with its content, so it's its scroll parent that scrolls.
  const updateAutoScroll = useCallback(
    (clientY: number) => {
      const direction = getAutoScrollDirection(
        clientY,
        getVisibleBounds(scrollParent.current),
        AUTO_SCROLL_EDGE,
      );

      if (direction === autoScrollDirection.current) return;

      stopAutoScroll();

      if (!direction) return;

      autoScrollDirection.current = direction;

      timeRef.current = setInterval(() => {
        scrollParent.current?.scrollBy({
          top: direction * AUTO_SCROLL_STEP,
          ...SMOOTH_SCROLL_CONFIGURATION,
        });
      }, 100);
    },
    [stopAutoScroll],
  );

  const onPointerMove = useCallback(
    (e: MouseEvent) => {
      const { clientX, clientY } = e;

      lastPointer.current = { clientX, clientY };

      prepareDraggableStyle(e);
      updateAutoScroll(clientY);
    },
    [prepareDraggableStyle, updateAutoScroll],
  );

  // Scrolling moves the rows under a still pointer.
  const onScroll = useCallback(() => {
    if (lastPointer.current) prepareDraggableStyle(lastPointer.current);
  }, [prepareDraggableStyle]);

  const onDrop = useCallback(
    (e: MouseEvent) => {
      if (isSelectioningOnDrag) {
        setIsSelectioningOnDrag(false);
      }

      if (isDragging) {
        const rect = docListRef.current?.getBoundingClientRect();

        if (isPointerInsideContainer(e, rect)) {
          const row = getRowAtY(rows.current, e.clientY - rect.top);

          if (row) {
            onDragMove(row.node);
          }
        }

        // The mousemove listener is detached once the drag ends, so the drop
        // highlight has to be cleared here.
        resetContainerStyles();

        setIsDragging(false);
      }
    },
    [isDragging, isSelectioningOnDrag, onDragMove, resetContainerStyles],
  );

  // The list stretches to the bottom of the panel, so a press on its blank
  // area counts as a press outside of it.
  const getIsBackgroundPress = useCallback((e: MouseEvent) => {
    const list = docListRef.current;

    if (e.target === list) return true;

    const actionMenu = document.getElementsByClassName('action-menu')?.[0];

    return (
      !isPointerInsideContainer(e, list?.getBoundingClientRect()) &&
      !isPointerInsideContainer(e, actionMenu?.getBoundingClientRect())
    );
  }, []);

  const isDraggingOrSelecting = isDragging || isSelectioningOnDrag;

  // Deselecting and starting a selection share one listener: with two, the
  // re-render from the first detached the second before it ran.
  const onMouseDown = useCallback(
    (e: MouseEvent) => {
      const list = docListRef.current;

      if (
        !list ||
        isDraggingOrSelecting ||
        isOpenDrawerOrModal() ||
        !getIsBackgroundPress(e)
      )
        return;

      setSelected((prev) => (getHasElementsInArray(prev) ? [] : prev));

      if (e.button !== 0 || isDataRoomViewer(role)) return;

      const { top, left } = list.getBoundingClientRect();

      selectionStart.current = { x: e.clientX - left, y: e.clientY - top };
      selectedIdsKey.current = '';

      setIsSelectioningOnDrag(true);
    },
    [isDraggingOrSelecting, role, getIsBackgroundPress, setSelected],
  );

  // Rows are measured once per drag rather than on every mousemove, and again
  // only if the nodes change mid-drag.
  useEffect(() => {
    if (!isDraggingOrSelecting || !docListRef.current) return;

    rows.current = measureRows(docListRef.current, currentlyDisplayedNodes);
    scrollParent.current = getScrollParent(docListRef.current);
  }, [isDraggingOrSelecting, currentlyDisplayedNodes]);

  useEffect(() => {
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mouseup', onDrop);

    // `prepareDraggableStyle` measures the list and writes inline styles, which
    // forces a layout on every event. Bound only while a drag is in progress,
    // it no longer makes plain mouse movement over a large folder expensive.
    if (isDraggingOrSelecting) {
      document.addEventListener('mousemove', onPointerMove);
      // Capturing, so it catches whichever ancestor does the scrolling.
      document.addEventListener('scroll', onScroll, true);
    } else {
      stopAutoScroll();
      lastPointer.current = null;
    }

    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onPointerMove);
      document.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('mouseup', onDrop);
    };
  }, [
    isDraggingOrSelecting,
    onPointerMove,
    onScroll,
    onDrop,
    onMouseDown,
    stopAutoScroll,
  ]);

  useEffect(() => stopAutoScroll, [stopAutoScroll]);

  return {
    isDragging,
    isSelectioningOnDrag,
    isRequestPending,
    draggableOverlayRef,
    selectionRectRef,
    docListRef,
    setIsDragging,
    setIsRequestPending,
  };
}
